import { createContext, useContext, useEffect, useState } from "react";

export type Palette = "default" | "aurora" | "paper" | "terminal" | "classic-blue";

export const PALETTES: { id: Palette; label: string; emoji: string; description: string }[] = [
  { id: "default", label: "Default", emoji: "🌊", description: "Cool slate with teal primary" },
  { id: "aurora", label: "Aurora", emoji: "🌌", description: "Magenta + green night sky" },
  { id: "paper", label: "Paper", emoji: "📜", description: "Warm cream with ink-gray text" },
  { id: "terminal", label: "Terminal", emoji: "🖥️", description: "Monospace green-on-black" },
  { id: "classic-blue", label: "Classic Blue", emoji: "🟦", description: "Deep navy with sky accents" },
];

interface PaletteContextValue {
  palette: Palette;
  setPalette: (p: Palette) => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

export function PaletteProvider({ children }: { children: React.ReactNode }) {
  const [palette, setPaletteState] = useState<Palette>("default");

  useEffect(() => {
    const stored = localStorage.getItem("vertali_palette") as Palette | null;
    if (stored && PALETTES.some((p) => p.id === stored)) {
      setPaletteState(stored);
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-palette", palette);
  }, [palette]);

  const setPalette = (p: Palette) => {
    setPaletteState(p);
    localStorage.setItem("vertali_palette", p);
  };

  return (
    <PaletteContext.Provider value={{ palette, setPalette }}>{children}</PaletteContext.Provider>
  );
}

export function usePalette(): PaletteContextValue {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    return {
      palette: "default",
      setPalette: () => {
        /* noop */
      },
    };
  }
  return ctx;
}
